import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Banner, Spinner } from '../components/States';
import type { RulesResponse } from '../api/types';
import { trackLoginError, trackSignUp } from '../lib/tracking';
import { AoAparecer } from '../lib/tracking/react';

/**
 * Primeiro acesso: trade link, contato e aceite das regras.
 *
 * A tela é obrigatória antes de negociar — é aqui que o cliente declara para
 * onde as skins vão e assume a regra de não trocar o nome da Steam depois.
 */
export function SignupPage() {
  const { user, loading, loginUrl, setUser } = useAuth();
  const navigate = useNavigate();

  const [rules, setRules] = useState<RulesResponse | null>(null);
  const [tradeUrl, setTradeUrl] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.rules().then(setRules).catch(() => setRules(null));
  }, []);

  useEffect(() => {
    if (user) {
      setTradeUrl((v) => v || user.tradeUrl || '');
      setWhatsapp((v) => v || user.whatsapp || '');
    }
  }, [user]);

  if (loading) return <Spinner label="Carregando a sua conta…" />;

  if (!user) {
    return (
      <div className="panel narrow">
        <h2>Entre para continuar</h2>
        <p className="muted">O cadastro é feito com a sua conta Steam. Não criamos senha nenhuma.</p>
        <a className="btn primary" href={loginUrl}>
          Entrar com a Steam
        </a>
      </div>
    );
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const { user: updated } = await api.onboarding({ tradeUrl: tradeUrl.trim(), whatsapp, acceptRules: true });
      trackSignUp();
      setUser(updated);
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não consegui salvar o cadastro. Tente de novo.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="panel narrow">
      <h2>Complete o seu cadastro</h2>
      <p className="muted">
        Você entrou como <b>{user.personaName ?? user.steamId}</b>. Falta o trade link e o aceite das regras.
      </p>

      {error && <Banner kind="error">{error}</Banner>}

      <form onSubmit={submit} className="form">
        <label className="field">
          <span>
            Trade link <b className="req">obrigatório</b>
          </span>
          <input
            type="url"
            value={tradeUrl}
            onChange={(e) => setTradeUrl(e.target.value)}
            placeholder="https://steamcommunity.com/tradeoffer/new/?partner=…&token=…"
            required
          />
          {/* /id/me resolve para o perfil de quem está logado, então o link
              serve para qualquer cliente sem precisar montar a URL na mão. */}
          <a
            className="helper-link"
            href="https://steamcommunity.com/id/me/tradeoffers/privacy"
            target="_blank"
            rel="noreferrer noopener"
          >
            🔗 Abrir a página do meu trade link na Steam
          </a>
          <small>
            A página abre em outra aba, já na sua conta. Copie o endereço no campo{' '}
            <i>URL de oferta de troca</i> e cole aqui. Conferimos se o link é mesmo da conta com que você entrou.
          </small>
        </label>

        <label className="field">
          <span>WhatsApp</span>
          <input
            type="tel"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder="(11) 90000-0000"
          />
          <small>Para falarmos com você sobre o pedido. Opcional.</small>
        </label>

        <div className="rules-box">
          <h3>Regras</h3>
          {rules ? (
            rules.rules.map((r) => (
              <div key={r.title} className="rule">
                <b>{r.title}</b>
                <p>{r.body}</p>
              </div>
            ))
          ) : (
            <p className="muted">Não consegui carregar o texto das regras. Recarregue a página antes de aceitar.</p>
          )}
        </div>

        <label className="check">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          <span>
            Li e aceito as regras, incluindo <b>não alterar o nome da minha conta Steam</b> depois de informar o trade
            link.
          </span>
        </label>

        <button className="btn primary" type="submit" disabled={saving || !accepted || !tradeUrl.trim() || !rules}>
          {saving ? 'Salvando…' : 'Concluir cadastro'}
        </button>
      </form>
    </div>
  );
}

/**
 * A Steam confirmou o login, mas o banco recusou gravar a conta ou a sessão.
 *
 * O código é o SQLSTATE do Postgres. A tradução existe para quem administra a
 * loja saber onde mexer sem abrir o log; o código vai junto para quando a
 * mensagem não bastar.
 */
function motivoGravacao(codigo: string | null): string {
  const c = codigo ?? 'desconhecido';
  const causa =
    c === '25006'
      ? 'o banco está em modo só leitura (limite de armazenamento do plano)'
      : c === '42501'
        ? 'o usuário do banco não tem permissão de escrita'
        : c === '42P01' || c === '42703'
          ? 'falta tabela ou coluna no banco (migrations não aplicadas)'
          : c === '53100'
            ? 'o disco do banco está cheio'
            : c === '53300'
              ? 'o banco atingiu o limite de conexões'
              : c.startsWith('08') || c.startsWith('57')
                ? 'a conexão com o banco caiu'
                : 'o banco recusou a gravação';
  return `Não foi possível entrar: ${causa}. Código ${c}.`;
}

/** Retorno do login: só existe para mostrar o motivo quando algo dá errado. */
export function LoginPage() {
  const { user, loading, loginUrl, dbAvailable } = useAuth();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const erro = params.get('erro');

  useEffect(() => {
    if (!loading && user && !erro) navigate(user.needsOnboarding ? '/cadastro' : '/');
  }, [loading, user, erro, navigate]);

  const mensagem =
    erro === 'sem-banco'
      ? 'O banco de dados está fora do ar, então não é possível entrar agora. Tente em alguns minutos.'
      : erro === 'bloqueado'
        ? 'Esta conta está bloqueada. Fale com o suporte.'
        : erro === 'steam'
          ? `A Steam não confirmou o login. ${params.get('motivo') ?? ''}`
          : erro === 'gravacao'
            ? motivoGravacao(params.get('codigo'))
            : null;

  return (
    <div className="panel narrow">
      <h2>Entrar</h2>
      {/* O login voltou com erro: registra o tipo e o código (SQLSTATE, sem
          dado pessoal). O "motivo" da Steam não vai, porque é texto livre. */}
      {erro && <AoAparecer disparar={() => trackLoginError(erro, params.get('codigo'))} />}
      {mensagem && <Banner kind="error">{mensagem}</Banner>}
      {!dbAvailable && !mensagem && (
        <Banner kind="warn">O banco está indisponível. O login não vai funcionar até ele voltar.</Banner>
      )}
      <p className="muted">
        Usamos a sua conta Steam para identificar você. Não pedimos senha, e não temos acesso a ela em momento algum.
      </p>
      <a className="btn primary" href={loginUrl}>
        Entrar com a Steam
      </a>
    </div>
  );
}
